/**
 * scheduleAssignmentDefaults.ts
 * Default allocation percentages for the staffing flows.
 *
 * Restored for the Staffing Allocation % reinstatement (PR B,
 * qa/staffing-allocation-2026-07-13). Wave 3 had pinned every assignment to
 * 100% and retired these defaults; the per-week WO/quote roster brings back
 * intentional part-time allocation, so a mentor added purely to satisfy the
 * L3+ skill gate defaults to a LIGHT 25% (oversight, not hands-on work) while
 * everyone else defaults to 100%.
 *
 * Dependency-free so it can be unit-tested directly.
 */

/** The set of Tier values that carry a 25% senior/oversight default. */
const SENIOR_TIERS = new Set(['Senior', 'Lead']);

/**
 * A mentor added to satisfy the L3+ skill gate supervises (QC / oversight)
 * rather than doing the hands-on work, so they default to a LIGHT allocation
 * regardless of tier. Skill level (L3+) ≠ Tier, so this is applied by the
 * roster's mentor heuristic (see rosterAllocation.defaultRosterPercent), not by
 * defaultPercentForTier.
 */
export const MENTOR_DEFAULT_PERCENT = 25;

/**
 * Jul-23 meeting quick-adds (2026-07-26): a REVIEWER carries light ongoing
 * oversight (Gigi: "10 or 25 or 50 — depends on the project", so the picker's
 * % stays editable); an APPROVER is a thin end-of-line share whose presence on
 * the plan mainly makes their availability visible ("if Ian's away, I need to
 * know before the reports pile up").
 */
export const REVIEWER_DEFAULT_PERCENT = 10;
export const APPROVER_DEFAULT_PERCENT = 5;

/** The full-time default for the person actually running the work. */
export const FULL_ALLOCATION_PERCENT = 100;

/**
 * Return the default allocation percentage for a user Tier.
 *   - Lead / Senior → 25 (oversight / QC)
 *   - Tech / Junior / null → 100 (hands-on)
 */
export function defaultPercentForTier(tier: string | null | undefined): number {
  if (tier && SENIOR_TIERS.has(tier)) return MENTOR_DEFAULT_PERCENT;
  return FULL_ALLOCATION_PERCENT;
}
